<template>
  <div class="container-fluid py-4">
    <div class="card">
      <div class="card-header pb-0">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h6>{{ companyName }} 품목 일괄 수정</h6>
            <p class="text-sm text-muted mb-0">엑셀에서 복사한 데이터를 붙여넣기하여 품목을 추가/수정할 수 있습니다.</p>
          </div>
          <div class="button-group">
            <a-button @click="goBack">
              <i class="fa-solid fa-arrow-left" />
              목록으로
            </a-button>
            <a-button type="primary" class="yellow-button" :loading="saveLoading" @click="handleSaveAll">
              <i class="fa-solid fa-floppy-disk" />
              변경사항 저장
            </a-button>
          </div>
        </div>
      </div>

      <div class="card-body">
        <!-- 제품명 선택 -->
        <div class="product-select-section mb-4">
          <a-row :gutter="16" align="middle">
            <a-col :span="6">
              <div class="select-label">제품명 선택 <span class="required">*</span></div>
            </a-col>
            <a-col :span="10">
              <a-select
                v-model:value="selectedProduct"
                placeholder="제품명을 선택하세요"
                style="width: 100%"
                :loading="productLoading"
                show-search
                :filter-option="filterOption"
                :options="productOptions"
                @change="handleProductChange"
              />
            </a-col>
            <a-col :span="8">
              <a-button type="dashed" @click="showAddProductModal" :disabled="!companyName">
                <i class="fa-solid fa-plus" /> 새 제품명 추가
              </a-button>
            </a-col>
          </a-row>
        </div>

        <!-- 붙여넣기 영역 (제품명 선택 후 표시) -->
        <div class="paste-section mb-4" v-if="selectedProduct">
          <div class="paste-header">
            <i class="fa-solid fa-paste" /> 엑셀 데이터 붙여넣기
          </div>
          <div class="paste-area">
            <div class="paste-guide">
              <strong>사용 방법:</strong> 엑셀에서 <code>제품사양</code> 열을 복사(Ctrl+C)하여 아래에 붙여넣기(Ctrl+V) 하세요.
              <br />
              <span class="example-text">예시: ∅3.2 ⨉ 10</span>
            </div>
            <a-textarea
              v-model:value="pasteData"
              placeholder="엑셀에서 복사한 제품사양을 여기에 붙여넣기 하세요 (Ctrl+V)&#10;&#10;∅3.2 ⨉ 10&#10;∅3.2 ⨉ 11.5&#10;∅3.2 ⨉ 13"
              :rows="6"
              class="paste-textarea"
            />
            <div class="paste-actions">
              <a-button type="primary" class="yellow-button" @click="parseAndAddItems" :disabled="!pasteData">
                <i class="fa-solid fa-plus" />
                목록에 추가 ({{ parsedRowCount }}건)
              </a-button>
              <a-button @click="pasteData = ''">
                초기화
              </a-button>
            </div>
          </div>
        </div>

        <!-- 비교 테이블 영역 (기존목록 / 신규목록) -->
        <div class="comparison-tables" v-if="selectedProduct">
          <a-row :gutter="16">
            <!-- 기존목록 테이블 -->
            <a-col :span="12">
              <div class="table-section existing-section">
                <div class="table-section-header">
                  <div class="section-title">
                    <i class="fa-solid fa-database" />
                    기존목록
                  </div>
                  <div class="section-count">
                    <span>{{ existingItems.length }}개</span>
                    <span v-if="modifiedCount > 0" class="modified-badge">
                      수정 {{ modifiedCount }}
                    </span>
                    <span v-if="deletedCount > 0" class="deleted-badge">
                      삭제예정 {{ deletedCount }}
                    </span>
                  </div>
                </div>
                <div class="editable-table-wrapper" v-if="existingItems.length > 0 || specLoading">
                  <a-table
                    :columns="existingColumns"
                    :data-source="existingItems"
                    :pagination="false"
                    :loading="specLoading"
                    size="small"
                    bordered
                    :row-class-name="getRowClassName"
                    row-key="_id"
                  >
                    <template #bodyCell="{ column, record }">
                      <!-- 제품사양 -->
                      <template v-if="column.dataIndex === 'spec'">
                        <div class="spec-cell">
                          <a-input
                            v-model:value="record.spec"
                            size="small"
                            @change="markAsModified(record)"
                            placeholder="제품사양"
                            :class="{ 'duplicate-input': isDuplicate(record), 'deleted-input': record._isDeleted }"
                            :disabled="record._isDeleted"
                          />
                          <span v-if="record._isDeleted" class="deleted-mark-badge" title="저장 시 삭제됩니다">
                            <i class="fa-solid fa-trash" /> 삭제예정
                          </span>
                          <span v-else-if="isDuplicate(record)" class="duplicate-badge" title="신규 목록에 동일한 사양이 있습니다">
                            <i class="fa-solid fa-clone" /> 중복
                          </span>
                        </div>
                      </template>

                      <!-- 삭제/복원 버튼 -->
                      <template v-else-if="column.dataIndex === 'action'">
                        <a-button
                          v-if="record._isDeleted"
                          type="text"
                          size="small"
                          @click="restoreExistingItem(record)"
                          title="삭제 취소"
                        >
                          <i class="fa-solid fa-rotate-left" style="color: #1890ff;" />
                        </a-button>
                        <a-button
                          v-else
                          type="text"
                          danger
                          size="small"
                          @click="markAsDeleted(record)"
                          title="삭제 예정으로 표시"
                        >
                          <i class="fa-solid fa-trash" />
                        </a-button>
                      </template>
                    </template>
                  </a-table>
                </div>
                <div class="table-empty-state" v-else>
                  <div class="empty-icon">
                    <i class="fa-solid fa-inbox" />
                  </div>
                  <p class="empty-text">등록된 품목이 없습니다</p>
                </div>
              </div>
            </a-col>

            <!-- 신규목록 테이블 -->
            <a-col :span="12">
              <div class="table-section new-section">
                <div class="table-section-header">
                  <div class="section-title">
                    <i class="fa-solid fa-plus-circle" />
                    신규목록
                  </div>
                  <div class="section-actions">
                    <span class="section-count">{{ newItems.length }}개</span>
                    <a-button size="small" @click="addEmptyRow" type="dashed">
                      <i class="fa-solid fa-plus" /> 빈 행 추가
                    </a-button>
                    <a-popconfirm
                      title="신규 품목을 모두 삭제하시겠습니까?"
                      ok-text="전체 삭제"
                      cancel-text="취소"
                      @confirm="deleteAllNewItems"
                      :disabled="newItems.length === 0"
                    >
                      <a-button size="small" danger :disabled="newItems.length === 0">
                        <i class="fa-solid fa-trash" />
                      </a-button>
                    </a-popconfirm>
                  </div>
                </div>
                <div class="editable-table-wrapper" v-if="newItems.length > 0">
                  <a-table
                    :columns="newColumns"
                    :data-source="newItems"
                    :pagination="false"
                    size="small"
                    bordered
                    :row-class-name="() => 'new-row'"
                    row-key="_id"
                  >
                    <template #bodyCell="{ column, record }">
                      <!-- 제품사양 -->
                      <template v-if="column.dataIndex === 'spec'">
                        <div class="spec-cell">
                          <a-input
                            v-model:value="record.spec"
                            size="small"
                            placeholder="제품사양 (예: ∅3.2 ⨉ 10)"
                            :class="{ 'duplicate-input': isDuplicate(record) }"
                          />
                          <span v-if="isDuplicate(record)" class="duplicate-badge duplicate-warning" title="기존 목록에 동일한 사양이 있습니다">
                            <i class="fa-solid fa-exclamation-triangle" /> 중복
                          </span>
                        </div>
                      </template>

                      <!-- 삭제 버튼 -->
                      <template v-else-if="column.dataIndex === 'action'">
                        <a-button type="text" danger size="small" @click="deleteNewItem(record)">
                          <i class="fa-solid fa-trash" />
                        </a-button>
                      </template>
                    </template>
                  </a-table>
                </div>
                <div class="table-empty-state" v-else>
                  <div class="empty-icon">
                    <i class="fa-solid fa-paste" />
                  </div>
                  <p class="empty-text">엑셀에서 복사하여 붙여넣기 하세요</p>
                </div>
              </div>
            </a-col>
          </a-row>

          <!-- 하단 저장 버튼 -->
          <div class="bottom-actions">
            <div class="save-summary">
              <span v-if="modifiedCount > 0" class="modified-badge">수정 {{ modifiedCount }}건</span>
              <span v-if="deletedCount > 0" class="deleted-badge">삭제 {{ deletedCount }}건</span>
              <span v-if="newItems.length > 0" class="new-badge">신규 {{ newItems.length }}건</span>
            </div>
            <a-button type="primary" class="yellow-button" :loading="saveLoading" @click="handleSaveAll">
              <i class="fa-solid fa-floppy-disk" />
              변경사항 저장
            </a-button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="!selectedProduct" class="empty-state">
          <a-empty :description="productLoading ? '제품명 목록을 불러오는 중...' : '제품명을 선택하면 품목 목록이 표시됩니다'" />
        </div>
      </div>
    </div>

    <!-- 새 제품명 추가 모달 -->
    <a-modal
      v-model:visible="addProductModalVisible"
      :footer="null"
      @cancel="addProductModalVisible = false"
    >
      <template #title>
        <div class="modal-title">새 제품명 추가</div>
      </template>
      <div class="modal-content" style="padding: 0 20px; border-radius: 0;">
        <a-form layout="vertical">
          <a-form-item label="제품명">
            <a-input v-model:value="newProductName" placeholder="새 제품명 입력" />
          </a-form-item>
        </a-form>
        <div class="modal-footer" style="padding:0;">
          <a-button @click="addProductModalVisible = false">취소</a-button>
          <a-button type="primary" class="yellow-button" @click="handleAddProduct">추가</a-button>
        </div>
      </div>
    </a-modal>

    <!-- 저장 확인 모달 -->
    <a-modal
      v-model:visible="saveConfirmModalVisible"
      title="변경사항 확인"
      width="700px"
      :footer="null"
      @cancel="saveConfirmModalVisible = false"
    >
      <div class="save-confirm-content">
        <p class="confirm-description">
          저장하기 전에 변경사항을 확인해주세요.
        </p>

        <!-- 요약 정보 -->
        <div class="summary-cards">
          <div class="summary-card existing-card">
            <div class="card-icon"><i class="fa-solid fa-database" /></div>
            <div class="card-info">
              <div class="card-number">{{ saveSummary.existingCount }}</div>
              <div class="card-label">기존 유지</div>
            </div>
          </div>
          <div class="summary-card modified-card">
            <div class="card-icon"><i class="fa-solid fa-pen" /></div>
            <div class="card-info">
              <div class="card-number">{{ saveSummary.modifiedCount }}</div>
              <div class="card-label">수정됨</div>
            </div>
          </div>
          <div class="summary-card delete-card">
            <div class="card-icon"><i class="fa-solid fa-trash" /></div>
            <div class="card-info">
              <div class="card-number">{{ saveSummary.deletedCount }}</div>
              <div class="card-label">삭제 예정</div>
            </div>
          </div>
          <div class="summary-card duplicate-card">
            <div class="card-icon"><i class="fa-solid fa-clone" /></div>
            <div class="card-info">
              <div class="card-number">{{ saveSummary.duplicateCount }}</div>
              <div class="card-label">중복 (제외)</div>
            </div>
          </div>
          <div class="summary-card new-card">
            <div class="card-icon"><i class="fa-solid fa-plus" /></div>
            <div class="card-info">
              <div class="card-number">{{ saveSummary.newCount }}</div>
              <div class="card-label">신규 추가</div>
            </div>
          </div>
        </div>

        <!-- 통합 상세 목록 -->
        <div class="detail-sections">
          <div class="merged-list-header">
            <span>전체 목록 ({{ mergedSortedItems.length }}개)</span>
            <div class="legend">
              <span class="legend-item existing"><i class="fa-solid fa-database" /> 기존</span>
              <span class="legend-item modified"><i class="fa-solid fa-pen" /> 수정</span>
              <span class="legend-item deleted"><i class="fa-solid fa-trash" /> 삭제</span>
              <span class="legend-item duplicate"><i class="fa-solid fa-clone" /> 중복</span>
              <span class="legend-item new"><i class="fa-solid fa-plus" /> 신규</span>
            </div>
          </div>
          <ul class="merged-item-list">
            <li v-for="item in mergedSortedItems" :key="item._id" :class="'item-' + item._status">
              <span class="item-spec" :class="{ 'strike': item._status === 'duplicate' || item._status === 'deleted' }">
                {{ item.spec }}
              </span>
              <span class="item-badge" :class="'badge-' + item._status">
                <template v-if="item._status === 'existing'">
                  <i class="fa-solid fa-database" /> 기존
                </template>
                <template v-else-if="item._status === 'modified'">
                  <i class="fa-solid fa-pen" /> 수정
                </template>
                <template v-else-if="item._status === 'deleted'">
                  <i class="fa-solid fa-trash" /> 삭제
                </template>
                <template v-else-if="item._status === 'duplicate'">
                  <i class="fa-solid fa-clone" /> 중복
                </template>
                <template v-else-if="item._status === 'new'">
                  <i class="fa-solid fa-plus" /> 신규
                </template>
              </span>
            </li>
          </ul>
        </div>

        <!-- 버튼 영역 -->
        <div class="modal-footer">
          <a-button @click="saveConfirmModalVisible = false">취소</a-button>
          <a-button
            type="primary"
            class="yellow-button"
            :loading="saveLoading"
            @click="executeSave"
          >
            <i class="fa-solid fa-check" /> 확인 후 저장
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message, Modal } from 'ant-design-vue';
import apiInstance from '@/axios';

export default {
  name: 'ItemBulkEdit',
  setup() {
    const router = useRouter();
    const route = useRoute();

    const productLoading = ref(false);
    const specLoading = ref(false);
    const saveLoading = ref(false);
    const companyName = ref('');
    const products = ref([]);
    const selectedProduct = ref(null);
    const items = ref([]);
    const pasteData = ref('');

    // 새 제품명 추가 모달
    const addProductModalVisible = ref(false);
    const newProductName = ref('');

    // 저장 확인 모달
    const saveConfirmModalVisible = ref(false);
    const saveSummary = ref({
      existingCount: 0,
      modifiedCount: 0,
      deletedCount: 0,
      duplicateCount: 0,
      newCount: 0,
      existingItems: [],
      modifiedItems: [],
      deletedItems: [],
      duplicateItems: [],
      newItems: [],
    });

    // 스펙 정렬 함수 (숫자 기준 자연 정렬)
    const sortBySpec = (items) => {
      return [...items].sort((a, b) => {
        const specA = a.spec || '';
        const specB = b.spec || '';
        // 숫자를 추출해서 비교 (자연 정렬)
        const numA = parseFloat(specA.match(/[\d.]+/)?.[0] || '0');
        const numB = parseFloat(specB.match(/[\d.]+/)?.[0] || '0');
        if (numA !== numB) return numA - numB;
        return specA.localeCompare(specB);
      });
    };

    // 정렬된 목록 computed
    const sortedExistingItems = computed(() => sortBySpec(saveSummary.value.existingItems));
    const sortedModifiedItems = computed(() => sortBySpec(saveSummary.value.modifiedItems));
    const sortedDuplicateItems = computed(() => sortBySpec(saveSummary.value.duplicateItems));
    const sortedNewItems = computed(() => sortBySpec(saveSummary.value.newItems));

    // 통합 정렬 목록 (기존 + 수정 + 삭제 + 중복 + 신규 모두 합쳐서 정렬)
    const mergedSortedItems = computed(() => {
      const merged = [];

      // 기존 유지 항목
      saveSummary.value.existingItems.forEach(item => {
        merged.push({ ...item, _status: 'existing' });
      });

      // 수정된 항목
      saveSummary.value.modifiedItems.forEach(item => {
        merged.push({ ...item, _status: 'modified' });
      });

      // 삭제 예정 항목
      saveSummary.value.deletedItems.forEach(item => {
        merged.push({ ...item, _status: 'deleted' });
      });

      // 중복 항목
      saveSummary.value.duplicateItems.forEach(item => {
        merged.push({ ...item, _status: 'duplicate' });
      });

      // 신규 항목
      saveSummary.value.newItems.forEach(item => {
        merged.push({ ...item, _status: 'new' });
      });

      // 전체 정렬
      return sortBySpec(merged);
    });

    // 기존목록 테이블 컬럼
    const existingColumns = [
      { title: '번호', dataIndex: '_existingIndex', width: 50, align: 'center' },
      { title: '제품사양', dataIndex: 'spec' },
      { title: '', dataIndex: 'action', width: 50, align: 'center' },
    ];

    // 신규목록 테이블 컬럼
    const newColumns = [
      { title: '번호', dataIndex: '_newIndex', width: 50, align: 'center' },
      { title: '제품사양', dataIndex: 'spec' },
      { title: '', dataIndex: 'action', width: 50, align: 'center' },
    ];

    // 스펙 정규화 함수 (비교용 - 특수문자 통일)
    const normalizeSpec = (spec) => {
      if (!spec) return '';
      return spec
        .replace(/[∅ØøΦφ]/g, 'O')  // 지름 기호 통일
        .replace(/[⨉×xX]/g, 'x')    // 곱하기 기호 통일
        .replace(/\s+/g, '')         // 공백 제거
        .toLowerCase();
    };

    // 중복 체크 함수
    const isDuplicate = (item) => {
      const normalizedSpec = normalizeSpec(item.spec);
      if (!normalizedSpec) return false;

      if (item._isNew) {
        // 신규 아이템: 기존 목록에 같은 스펙이 있는지 확인
        return items.value.some(
          existing => !existing._isNew && normalizeSpec(existing.spec) === normalizedSpec
        );
      } else {
        // 기존 아이템: 신규 목록에 같은 스펙이 있는지 확인
        return items.value.some(
          newItem => newItem._isNew && normalizeSpec(newItem.spec) === normalizedSpec
        );
      }
    };

    // 아이템 코드 생성 함수
    const generateItemCode = (productName, spec, counter) => {
      // 제품명에서 알파벳과 숫자만 추출
      const productCode = productName
        .replace(/[^A-Za-z0-9]/g, '')
        .substring(0, 2)
        .toUpperCase();

      // 규격에서 숫자만 추출
      const numbers = spec.match(/\d+(\.\d+)?/g) || [];
      const specCode = numbers
        .map((n) => n.replace('.', ''))
        .join('')
        .substring(0, 4)
        .padStart(4, '0');

      // 기본 코드 생성
      let code = `IT${productCode}${specCode}`;

      // 중복 방지를 위한 카운터 추가
      if (counter) {
        code += counter.toString().padStart(2, '0');
      }

      return code;
    };

    // computed
    const productOptions = computed(() =>
      products.value.map(product => ({
        value: product.brand,
        label: `${product.brand} (${product.count}개)`,
      }))
    );

    // 기존 아이템 목록 (API에서 불러온 것)
    const existingItems = computed(() => {
      const filtered = items.value.filter(item => !item._isNew);
      // 인덱스를 원본 객체에 직접 업데이트
      filtered.forEach((item, idx) => {
        item._existingIndex = idx + 1;
      });
      return filtered;
    });

    // 신규 아이템 목록 (붙여넣기/빈 행 추가로 생성된 것)
    const newItems = computed(() => {
      const filtered = items.value.filter(item => item._isNew);
      // 인덱스를 원본 객체에 직접 업데이트
      filtered.forEach((item, idx) => {
        item._newIndex = idx + 1;
      });
      return filtered;
    });

    const modifiedCount = computed(() => items.value.filter(item => item._isModified && !item._isNew && !item._isDeleted).length);
    const deletedCount = computed(() => items.value.filter(item => item._isDeleted).length);
    const newItemCount = computed(() => items.value.filter(item => item._isNew).length);
    const parsedRowCount = computed(() => {
      if (!pasteData.value) return 0;
      return pasteData.value.trim().split('\n').filter(row => row.trim()).length;
    });

    // 셀렉트박스 필터 옵션
    const filterOption = (input, option) => {
      const searchText = input.toLowerCase();
      return (
        option.value.toLowerCase().includes(searchText) ||
        option.label.toLowerCase().includes(searchText)
      );
    };

    // 제품명 목록 로드 (POST /items/dent/2)
    const loadProducts = async () => {
      console.log('loadProducts 호출됨, companyName:', companyName.value);
      if (!companyName.value) {
        console.log('companyName이 비어있어서 리턴');
        return;
      }

      productLoading.value = true;
      try {
        console.log('API 호출 시작: /items/dent/2');
        const response = await apiInstance.post('/items/dent/2', {
          manufacturer: companyName.value,
        });

        console.log('제품명 API 전체 응답:', response);
        console.log('response.data:', response.data);
        console.log('response.data 타입:', typeof response.data);
        console.log('response.data가 배열?:', Array.isArray(response.data));

        // 다양한 응답 형식 처리
        let data = [];
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (response.data && Array.isArray(response.data.result)) {
          data = response.data.result;
        } else if (response.data && Array.isArray(response.data.items)) {
          data = response.data.items;
        }

        console.log('추출된 data:', data);

        // sortWeight 기준으로 정렬
        products.value = data.sort((a, b) => (a.sortWeight || 0) - (b.sortWeight || 0));
        console.log('products.value 설정 완료:', products.value);
        console.log('products.value 길이:', products.value.length);

        // 다음 틱에서 productOptions 확인
        setTimeout(() => {
          console.log('productOptions.value:', productOptions.value);
        }, 100);

        if (products.value.length === 0) {
          message.info('등록된 제품명이 없습니다. 새 제품명을 추가해주세요.');
        }
      } catch (error) {
        console.error('제품명 로드 실패:', error);
        console.error('에러 상세:', error.response?.data || error.message);
        message.error('제품명 목록을 불러오는데 실패했습니다.');
        products.value = [];
      } finally {
        productLoading.value = false;
      }
    };

    // 제품 사양 목록 로드 (POST /items/dent/3)
    const loadSpecifications = async () => {
      console.log('loadSpecifications 호출됨');
      console.log('selectedProduct.value:', selectedProduct.value);
      console.log('companyName.value:', companyName.value);

      if (!selectedProduct.value) {
        console.log('selectedProduct가 없어서 리턴');
        return;
      }

      specLoading.value = true;
      try {
        console.log('API 호출: /items/dent/3', {
          manufacturer: companyName.value,
          brand: selectedProduct.value,
        });

        const response = await apiInstance.post('/items/dent/3', {
          manufacturer: companyName.value,
          brand: selectedProduct.value,
        });

        console.log('품목 API 응답:', response);
        console.log('response.data:', response.data);

        // 다양한 응답 형식 처리
        let data = [];
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (response.data && Array.isArray(response.data.result)) {
          data = response.data.result;
        } else if (response.data && Array.isArray(response.data.items)) {
          data = response.data.items;
        }

        console.log('추출된 품목 data:', data);

        items.value = data.map((item, idx) => ({
          _id: item._id,
          manufacturer: item.manufacturer,
          brand: item.brand,
          spec: item.spec || item.itemName || '', // spec 또는 itemName 사용
          index: idx + 1,
          _isModified: false,
          _isNew: false,
          _originalData: { ...item },
        }));

        console.log('items.value 설정 완료:', items.value.length, '개');

        if (items.value.length > 0) {
          message.success(`${items.value.length}개 품목을 불러왔습니다.`);
        } else {
          message.info('등록된 품목이 없습니다. 엑셀에서 복사하여 붙여넣기 하세요.');
        }
      } catch (error) {
        console.error('품목 로드 실패:', error);
        console.error('에러 상세:', error.response?.data || error.message);
        message.error('품목을 불러오는데 실패했습니다.');
        items.value = [];
      } finally {
        specLoading.value = false;
      }
    };

    // 제품명 선택 변경
    const handleProductChange = (value) => {
      console.log('handleProductChange 호출됨, value:', value);
      items.value = [];
      pasteData.value = '';
      if (value) {
        loadSpecifications();
      }
    };

    // selectedProduct 변경 감지 (백업)
    watch(selectedProduct, (newVal) => {
      console.log('selectedProduct watch 트리거됨:', newVal);
      if (newVal && items.value.length === 0) {
        loadSpecifications();
      }
    });

    // 새 제품명 추가 모달 표시
    const showAddProductModal = () => {
      newProductName.value = '';
      addProductModalVisible.value = true;
    };

    // 새 제품명 추가
    const handleAddProduct = () => {
      if (!newProductName.value.trim()) {
        message.warning('제품명을 입력해주세요.');
        return;
      }

      // 중복 체크
      const exists = products.value.some(p => p.brand === newProductName.value.trim());
      if (exists) {
        message.warning('이미 존재하는 제품명입니다.');
        return;
      }

      const newProduct = {
        brand: newProductName.value.trim(),
        count: 0,
        sortWeight: products.value.length + 1,
      };
      products.value.push(newProduct);
      selectedProduct.value = newProduct.brand;
      items.value = [];
      addProductModalVisible.value = false;
      message.success(`"${newProduct.brand}" 제품명이 추가되었습니다.`);
    };

    // 빈 행 추가
    const addEmptyRow = () => {
      const newItem = {
        _id: `new_${Date.now()}`,
        index: items.value.length + 1,
        manufacturer: companyName.value,
        brand: selectedProduct.value,
        spec: '',
        _isNew: true,
        _isModified: false,
      };
      items.value.push(newItem);
    };

    // 신규 목록 전체 삭제
    const deleteAllNewItems = () => {
      items.value = items.value.filter(item => !item._isNew);
      message.success('신규 품목이 모두 삭제되었습니다.');
    };

    // 기존 항목 삭제 예정 표시
    const markAsDeleted = (record) => {
      record._isDeleted = true;
      message.info('삭제 예정으로 표시되었습니다. 저장 시 삭제됩니다.');
    };

    // 기존 항목 삭제 취소 (복원)
    const restoreExistingItem = (record) => {
      record._isDeleted = false;
      message.success('삭제가 취소되었습니다.');
    };

    // 신규 항목 삭제
    const deleteNewItem = (record) => {
      items.value = items.value.filter(item => item._id !== record._id);
      message.success('품목이 제거되었습니다.');
    };

    // 수정됨 표시
    const markAsModified = (record) => {
      if (!record._isNew) {
        record._isModified = true;
      }
    };

    // 행 클래스
    const getRowClassName = (record) => {
      if (record._isDeleted) return 'deleted-row';
      if (record._isNew) return 'new-row';
      if (record._isModified) return 'modified-row';
      return '';
    };

    // 붙여넣은 데이터 파싱 및 추가
    const parseAndAddItems = () => {
      if (!pasteData.value.trim()) {
        message.warning('붙여넣을 데이터가 없습니다.');
        return;
      }

      if (!selectedProduct.value) {
        message.warning('제품명을 먼저 선택해주세요.');
        return;
      }

      const lines = pasteData.value.trim().split('\n');
      const newItems = [];

      lines.forEach((line, idx) => {
        const spec = line.trim();
        if (!spec) return;

        newItems.push({
          _id: `new_${Date.now()}_${idx}`,
          index: items.value.length + newItems.length + 1,
          manufacturer: companyName.value,
          brand: selectedProduct.value,
          spec,
          _isNew: true,
          _isModified: false,
        });
      });

      if (newItems.length > 0) {
        items.value = [...items.value, ...newItems];
        message.success(`${newItems.length}개 품목이 추가되었습니다.`);
        pasteData.value = '';
      } else {
        message.warning('추가할 품목이 없습니다.');
      }
    };


    // 저장 전 확인 (모달 표시)
    const handleSaveAll = () => {
      const modifiedItemsList = items.value.filter(item => item._isModified && !item._isNew && !item._isDeleted);
      const deletedItemsList = items.value.filter(item => item._isDeleted);
      const allNewItems = items.value.filter(item => item._isNew);

      // 중복 체크: 신규 항목 중 기존에 있는 것 분리
      const duplicateItems = [];
      const uniqueNewItems = [];

      allNewItems.forEach(newItem => {
        if (isDuplicate(newItem)) {
          duplicateItems.push(newItem);
        } else {
          uniqueNewItems.push(newItem);
        }
      });

      // 기존 항목 중 수정되지 않고 삭제 예정도 아닌 것
      const unchangedExisting = items.value.filter(item => !item._isNew && !item._isModified && !item._isDeleted);

      if (modifiedItemsList.length === 0 && uniqueNewItems.length === 0 && deletedItemsList.length === 0) {
        if (duplicateItems.length > 0) {
          message.warning(`신규 항목 ${duplicateItems.length}개가 모두 중복입니다. 저장할 항목이 없습니다.`);
        } else {
          message.warning('저장할 변경사항이 없습니다.');
        }
        return;
      }

      // 요약 정보 설정
      saveSummary.value = {
        existingCount: unchangedExisting.length,
        modifiedCount: modifiedItemsList.length,
        deletedCount: deletedItemsList.length,
        duplicateCount: duplicateItems.length,
        newCount: uniqueNewItems.length,
        existingItems: unchangedExisting,
        modifiedItems: modifiedItemsList,
        deletedItems: deletedItemsList,
        duplicateItems: duplicateItems,
        newItems: uniqueNewItems,
      };

      // 모달 표시
      saveConfirmModalVisible.value = true;
    };

    // 실제 저장 실행
    const executeSave = async () => {
      const { modifiedItems, deletedItems, newItems: uniqueNewItems } = saveSummary.value;

      saveLoading.value = true;
      let successCount = 0;
      let deleteCount = 0;
      let failCount = 0;

      try {
        // 삭제 예정 항목 삭제 (DELETE /items/:itemId)
        for (const item of deletedItems) {
          try {
            await apiInstance.delete(`/items/${item._id}`);
            deleteCount++;
          } catch (error) {
            console.error('삭제 실패:', item._id, error);
            failCount++;
          }
        }

        // 수정된 항목 저장 (PUT /items/:itemId)
        for (const item of modifiedItems) {
          try {
            await apiInstance.put(`/items/${item._id}`, {
              manufacturer: companyName.value,
              brand: item.brand,
              spec: item.spec,
            });
            item._isModified = false;
            item._originalData = { ...item };
            successCount++;
          } catch (error) {
            console.error('수정 실패:', item._id, error);
            failCount++;
          }
        }

        // 신규 항목 추가 (POST /items) - 중복 제외된 것만
        for (let i = 0; i < uniqueNewItems.length; i++) {
          const item = uniqueNewItems[i];
          try {
            const brand = item.brand;
            const spec = item.spec;
            const itemName = `${brand} ${spec}`;
            const itemCode = generateItemCode(brand, spec, i + 1);

            const payload = {
              companyName: companyName.value,
              medicalDept: 'DENT',
              productType: '임플란트',
              brand,
              itemSpec: spec,
              itemName,
              itemCode,
              description: `${itemName}은 ${companyName.value}의 공식제품입니다.`,
              duplicateYn: 'Y',
              unregisterYn: 'N',
            };

            console.log('신규 품목 등록 payload:', payload);

            const response = await apiInstance.post('/items/by-company-name', payload);
            if (response.data && response.data._id) {
              item._id = response.data._id;
              item._isNew = false;
              item._originalData = { ...item };
            }
            successCount++;
          } catch (error) {
            console.error('추가 실패:', item, error);
            failCount++;
          }
        }

        // 삭제된 항목과 중복 항목은 목록에서 제거
        const deletedIds = deletedItems.map(d => d._id);
        const duplicateIds = saveSummary.value.duplicateItems.map(d => d._id);
        items.value = items.value.filter(item => !deletedIds.includes(item._id) && !duplicateIds.includes(item._id));

        saveConfirmModalVisible.value = false;

        // 결과 메시지 생성
        const messages = [];
        if (successCount > 0) messages.push(`${successCount}개 저장`);
        if (deleteCount > 0) messages.push(`${deleteCount}개 삭제`);

        if (failCount === 0) {
          message.success(messages.join(', ') + ' 완료');
        } else {
          message.warning(`${messages.join(', ')}, ${failCount}개 실패`);
        }
      } finally {
        saveLoading.value = false;
      }
    };

    // 뒤로 가기
    const goBack = () => {
      const hasChanges = items.value.some(item => item._isModified || item._isNew || item._isDeleted);
      if (hasChanges) {
        Modal.confirm({
          title: '변경사항이 있습니다',
          content: '저장하지 않은 변경사항이 있습니다. 페이지를 떠나시겠습니까?',
          okText: '떠나기',
          cancelText: '취소',
          onOk: () => {
            router.push(`/item/list?companyName=${companyName.value}`);
          },
        });
      } else {
        router.push(`/item/list?companyName=${companyName.value}`);
      }
    };

    onMounted(() => {
      companyName.value = route.query.companyName || '';
      if (companyName.value) {
        loadProducts();
      }
    });

    return {
      productLoading,
      specLoading,
      saveLoading,
      companyName,
      products,
      productOptions,
      selectedProduct,
      items,
      existingItems,
      newItems,
      pasteData,
      existingColumns,
      newColumns,
      modifiedCount,
      deletedCount,
      newItemCount,
      parsedRowCount,
      addProductModalVisible,
      newProductName,
      saveConfirmModalVisible,
      saveSummary,
      sortedExistingItems,
      sortedModifiedItems,
      sortedDuplicateItems,
      sortedNewItems,
      mergedSortedItems,
      filterOption,
      handleProductChange,
      showAddProductModal,
      handleAddProduct,
      addEmptyRow,
      deleteAllNewItems,
      markAsDeleted,
      restoreExistingItem,
      deleteNewItem,
      markAsModified,
      isDuplicate,
      getRowClassName,
      parseAndAddItems,
      handleSaveAll,
      executeSave,
      goBack,
    };
  },
};
</script>

<style scoped>
.button-group {
  display: flex;
  gap: 8px;
}

.product-select-section {
  background: #f8f9fa;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

.select-label {
  font-weight: 500;
  color: #344767;
  font-size: 14px;
}

.select-label .required {
  color: #ff4d4f;
}

.paste-section {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

.paste-header {
  background: #1890ff;
  color: #fff;
  padding: 12px 16px;
  font-weight: 500;
  font-size: 14px;
}

.paste-header i {
  margin-right: 8px;
}

.paste-area {
  background: #fafafa;
  padding: 16px;
}

.paste-guide {
  margin-bottom: 12px;
  color: #666;
  font-size: 13px;
  line-height: 1.6;
}

.paste-guide code {
  background: #e8e8e8;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.example-text {
  color: #999;
  font-family: monospace;
  font-size: 12px;
}

.paste-textarea {
  font-family: monospace;
  font-size: 13px;
}

.paste-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}


.modified-badge {
  background: #fff7e6;
  color: #fa8c16;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #ffd591;
}

.new-badge {
  background: #f6ffed;
  color: #52c41a;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #b7eb8f;
}

.deleted-badge {
  background: #fff1f0;
  color: #ff4d4f;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #ffa39e;
}

.editable-table-wrapper {
  overflow-x: auto;
}

.editable-table-wrapper :deep(.modified-row) {
  background-color: #fff7e6 !important;
}

.editable-table-wrapper :deep(.deleted-row) {
  background-color: #fff1f0 !important;
}

.editable-table-wrapper :deep(.deleted-row td) {
  text-decoration: line-through;
  color: #999;
}

.editable-table-wrapper :deep(.new-row) {
  background-color: #f6ffed !important;
}

.editable-table-wrapper :deep(.ant-input) {
  border: 1px solid transparent;
  background: transparent;
}

.editable-table-wrapper :deep(.ant-input:hover),
.editable-table-wrapper :deep(.ant-input:focus) {
  border-color: #d9d9d9;
  background: #fff;
}

.empty-state {
  padding: 60px 0;
  text-align: center;
}

.bottom-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
}

.save-summary {
  display: flex;
  gap: 8px;
  align-items: center;
}

.yellow-button {
  background-color: #f5b100 !important;
  border-color: #f5b100 !important;
  color: #fff !important;
}

.yellow-button:hover {
  background-color: #d9a000 !important;
  border-color: #d9a000 !important;
}

/* 비교 테이블 영역 */
.comparison-tables {
  margin-top: 16px;
}

.table-section {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.table-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
}

.existing-section .table-section-header {
  background: #f0f5ff;
}

.new-section .table-section-header {
  background: #f6ffed;
}

.section-title {
  font-weight: 600;
  font-size: 14px;
  color: #344767;
  display: flex;
  align-items: center;
  gap: 8px;
}

.existing-section .section-title {
  color: #1890ff;
}

.new-section .section-title {
  color: #52c41a;
}

.section-title i {
  font-size: 16px;
}

.section-count {
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-section .editable-table-wrapper {
  flex: 1;
  padding: 0;
}

.table-section :deep(.ant-table-wrapper) {
  height: 100%;
}

.table-section :deep(.ant-table) {
  border-radius: 0;
}

.table-section :deep(.ant-table-container) {
  border-radius: 0;
}

.table-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  min-height: 200px;
}

.empty-icon {
  font-size: 32px;
  color: #d9d9d9;
  margin-bottom: 12px;
}

.empty-text {
  color: #999;
  font-size: 13px;
  margin: 0;
}

/* 중복 표시 스타일 */
.spec-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.spec-cell .ant-input {
  flex: 1;
}

.duplicate-input {
  border-color: #faad14 !important;
  background-color: #fffbe6 !important;
}

.duplicate-input:focus {
  border-color: #faad14 !important;
  box-shadow: 0 0 0 2px rgba(250, 173, 20, 0.2) !important;
}

.deleted-input {
  border-color: #ff4d4f !important;
  background-color: #fff1f0 !important;
  text-decoration: line-through;
  color: #999 !important;
}

.deleted-mark-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  background: #fff1f0;
  color: #ff4d4f;
  border: 1px solid #ffa39e;
}

.deleted-mark-badge i {
  font-size: 10px;
}

.duplicate-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.duplicate-badge i {
  font-size: 10px;
}

.duplicate-badge.duplicate-warning {
  background: #fffbe6;
  color: #d48806;
  border: 1px solid #ffe58f;
}

/* 저장 확인 모달 스타일 */
.save-confirm-content {
  padding: 0 8px;
}

.confirm-description {
  color: #666;
  margin-bottom: 20px;
}

.summary-cards {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.summary-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f5f5f5;
}

.summary-card .card-icon {
  font-size: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fff;
}

.summary-card .card-number {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.summary-card .card-label {
  font-size: 12px;
  color: #666;
}

.existing-card {
  background: #f0f5ff;
}

.existing-card .card-icon {
  color: #1890ff;
}

.modified-card {
  background: #fff7e6;
}

.modified-card .card-icon {
  color: #fa8c16;
}

.delete-card {
  background: #fff1f0;
}

.delete-card .card-icon {
  color: #ff4d4f;
}

.duplicate-card {
  background: #f5f5f5;
}

.duplicate-card .card-icon {
  color: #999;
}

.new-card {
  background: #f6ffed;
}

.new-card .card-icon {
  color: #52c41a;
}

.detail-sections {
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 20px;
}

.detail-section {
  margin-bottom: 16px;
}

.section-header {
  font-weight: 500;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.existing-header {
  background: #f0f5ff;
  color: #1890ff;
}

.modified-header {
  background: #fff7e6;
  color: #d46b08;
}

.duplicate-header {
  background: #fff1f0;
  color: #cf1322;
}

.new-header {
  background: #f6ffed;
  color: #389e0d;
}

.item-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 13px;
}

.item-list li {
  padding: 6px 12px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-list li:last-child {
  border-bottom: none;
}

.existing-list li {
  color: #1890ff;
}

.modified-list li {
  color: #d46b08;
}

.duplicate-list li {
  color: #999;
  text-decoration: line-through;
}

.dup-note {
  font-size: 11px;
  color: #ff4d4f;
  text-decoration: none;
}

.new-list li {
  color: #389e0d;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

/* 통합 목록 스타일 */
.merged-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px 8px 0 0;
  font-weight: 500;
  color: #333;
}

.legend {
  display: flex;
  gap: 12px;
  font-size: 12px;
  font-weight: normal;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
}

.legend-item.existing {
  background: #f0f5ff;
  color: #1890ff;
}

.legend-item.modified {
  background: #fff7e6;
  color: #fa8c16;
}

.legend-item.deleted {
  background: #fff1f0;
  color: #ff4d4f;
}

.legend-item.duplicate {
  background: #f5f5f5;
  color: #999;
}

.legend-item.new {
  background: #f6ffed;
  color: #52c41a;
}

.merged-item-list {
  list-style: none;
  padding: 0;
  margin: 0;
  border: 1px solid #e8e8e8;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 250px;
  overflow-y: auto;
}

.merged-item-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.merged-item-list li:last-child {
  border-bottom: none;
}

.merged-item-list li.item-existing {
  background: #fafcff;
}

.merged-item-list li.item-modified {
  background: #fffcf5;
}

.merged-item-list li.item-deleted {
  background: #fff1f0;
}

.merged-item-list li.item-duplicate {
  background: #fafafa;
}

.merged-item-list li.item-new {
  background: #f9fff6;
}

.item-spec {
  flex: 1;
  font-family: monospace;
}

.item-spec.strike {
  text-decoration: line-through;
  color: #999;
}

.item-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
}

.badge-existing {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.badge-modified {
  background: #fff7e6;
  color: #fa8c16;
  border: 1px solid #ffd591;
}

.badge-deleted {
  background: #fff1f0;
  color: #ff4d4f;
  border: 1px solid #ffa39e;
}

.badge-duplicate {
  background: #f5f5f5;
  color: #999;
  border: 1px solid #d9d9d9;
}

.badge-new {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}
</style>
